import { useState, useEffect, useRef } from "react";
import { Play, Pause, SkipBack, SkipForward, Radio, Music, Volume2, X, ChevronDown, ChevronUp, Timer } from "lucide-react";
import { PomodoroTimer } from "./PomodoroTimer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useAudio } from "@/contexts/AudioContext";
import { usePomodoro } from "@/contexts/PomodoroContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ATCPanel } from "./ATCPanel";
import { YouTubeRadio } from "./YouTubeRadio";
import YouTubeStreamsStatus from "./YouTubeStreamsStatus";
import PlayingIndicator from "@/components/PlayingIndicator";
import { supabase } from "@/integrations/supabase/client";
import { LiveListeners } from "./LiveListeners";
import { MobileMenuButton } from "./MobilePlayerLayout";
import { useIsMobile } from "@/hooks/use-mobile";

const formatTime = (seconds: number): string => {
  if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

interface MinimalisticPlayerProps {
  selectedAirport?: string;
  onAirportSelect: (airportCode: string) => void;
  videoUrl?: string | null;
}

export const MinimalisticPlayer = ({ selectedAirport, onAirportSelect, videoUrl }: MinimalisticPlayerProps) => {
const isMobile = useIsMobile();
  const [announcements, setAnnouncements] = useState<string[]>([]);
  const [showBanner, setShowBanner] = useState(() => {
    const dismissed = localStorage.getItem('announcement-banner-dismissed-permanently');
    return dismissed !== 'true';
  });
  
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);
  const [isYouTubeRadioPlaying, setIsYouTubeRadioPlaying] = useState(false);
  const [currentYouTubeStream, setCurrentYouTubeStream] = useState<{ id: string; name: string; description: string; videoId?: string } | null>(null);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [youtubeStreams, setYoutubeStreams] = useState<any[]>([]);
  const [currentStreamIndex, setCurrentStreamIndex] = useState(0);
  const [isMobilePlayerCollapsed, setIsMobilePlayerCollapsed] = useState(false);
  const mobileVideoRef = useRef<HTMLVideoElement>(null);
  
  // Pomodoro timer state from context (persists when sheet closes)
  const { timeLeft: pomodoroTime, isRunning: isPomodoroRunning, isComplete: isPomodoroComplete } = usePomodoro();
  
  const formatPomodoroIndicator = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins >= 60) {
      return `${Math.floor(mins / 60)}h`;
    }
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${secs}s`;
  };

  // Ensure mobile video plays when expanded
  useEffect(() => {
    if (isMobile && !isMobilePlayerCollapsed && mobileVideoRef.current) {
      mobileVideoRef.current.play().catch(() => {
        // Autoplay blocked, will play on user interaction
      });
    }
  }, [isMobile, isMobilePlayerCollapsed]);

  // Fetch announcements from database
  useEffect(() => {
    const fetchAnnouncements = async () => {
      const { data, error } = await supabase
        .from('announcements')
        .select('message')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (!error && data && data.length > 0) {
        setAnnouncements(data.map(a => a.message));
      }
    };

    fetchAnnouncements();
  }, []);

  // Fetch YouTube streams for skip functionality
  useEffect(() => {
    const fetchStreams = async () => {
      const { data } = await (supabase as any)
        .from('youtube_streams')
        .select('*')
        .eq('is_active', true)
        .order('id');
      
      if (data) {
        setYoutubeStreams(data);
      }
    };
    
    fetchStreams();
  }, []);

  const handleDismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem('announcement-banner-dismissed-permanently', 'true');
  };

  // Cycle through announcements every 5 seconds
  useEffect(() => {
    if (showBanner && announcements.length > 0) {
      const timer = setInterval(() => {
        setCurrentAnnouncementIndex((prev) => (prev + 1) % announcements.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [showBanner, announcements.length]);

  const {
    isPlaying,
    isAtcActive,
    isAtcLoading,
    musicVolume,
    soundVolume,
    currentAirportCode,
    playPause,
    setAtcStream,
    toggleAtc,
    setMusicVolume,
    setSoundVolume,
  } = useAudio();

  // Handle play/pause - sync YouTube radio and music playback
  const handlePlayPause = () => {
    // If YouTube is playing, toggle that; otherwise toggle music
    if (isYouTubeRadioPlaying) {
      const nextPlaying = !isPlaying;
      setIsAudioLoading(nextPlaying); // Show loading when starting to play
      playPause();
      window.dispatchEvent(new Event(nextPlaying ? 'yt-radio-play' : 'yt-radio-pause'));
    } else {
      setIsAudioLoading(true);
      playPause();
    }
  };

  // Handle skip to next YouTube stream
  const handleSkipForward = () => {
    if (isYouTubeRadioPlaying && youtubeStreams.length > 0) {
      const nextIndex = (currentStreamIndex + 1) % youtubeStreams.length;
      setCurrentStreamIndex(nextIndex);
      const nextStream = youtubeStreams[nextIndex];
      window.dispatchEvent(new CustomEvent('yt-radio-select-stream', { detail: { id: nextStream.id } }));
    }
  };

  // Handle skip to previous YouTube stream
  const handleSkipBack = () => {
    if (isYouTubeRadioPlaying && youtubeStreams.length > 0) {
      const prevIndex = (currentStreamIndex - 1 + youtubeStreams.length) % youtubeStreams.length;
      setCurrentStreamIndex(prevIndex);
      const prevStream = youtubeStreams[prevIndex];
      window.dispatchEvent(new CustomEvent('yt-radio-select-stream', { detail: { id: prevStream.id } }));
    }
  };

  // Clear loading state when playback state changes
  useEffect(() => {
    if (isPlaying || isYouTubeRadioPlaying) {
      const timer = setTimeout(() => setIsAudioLoading(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isPlaying, isYouTubeRadioPlaying]);

  // Sync isPlaying state with YouTube radio state
  useEffect(() => {
    if (isYouTubeRadioPlaying && !isPlaying) {
      playPause();
    }
  }, [isYouTubeRadioPlaying]);

  // Mobile full-screen layout with video backdrop
  if (isMobile) {
    return (
      <>
        {/* Hidden YouTube Radio - auto-plays on first play */}
        <div className="absolute w-px h-px -m-px overflow-hidden opacity-0 pointer-events-none -z-10" aria-hidden>
          <YouTubeRadio 
            volume={musicVolume} 
            onVolumeChange={setMusicVolume}
            onPlayingChange={setIsYouTubeRadioPlaying}
            autoPlay={isPlaying}
            onStreamChange={setCurrentYouTubeStream}
          />
        </div>

        <div className="fixed inset-0 flex flex-col overflow-hidden">
          {/* Video Background - fills entire screen on mobile */}
          {videoUrl && (
            <div className="absolute inset-0 z-0">
              <video
                ref={mobileVideoRef}
                className="w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                src={videoUrl}
                aria-label="Ambient airplane flying above clouds"
              >
                <track kind="captions" />
              </video>
              {/* Gradient overlay - adjusts based on collapsed state */}
              <div className={`absolute inset-0 transition-opacity duration-500 ${
                isMobilePlayerCollapsed 
                  ? 'bg-gradient-to-b from-transparent via-transparent to-background/95'
                  : 'bg-gradient-to-b from-background/20 via-background/40 to-background/70'
              }`} />
            </div>
          )}

          {/* Mobile player with smooth slide animation */}
          <div 
            className={`absolute inset-x-0 bottom-0 z-10 transition-transform duration-500 ease-out ${
              isMobilePlayerCollapsed ? 'translate-y-0' : 'translate-y-0'
            }`}
            style={{
              height: isMobilePlayerCollapsed ? 'auto' : '100%',
            }}
          >
            {/* Tap backdrop to expand (only when collapsed) */}
            {isMobilePlayerCollapsed && (
              <div 
                className="fixed inset-0 z-0" 
                onClick={() => setIsMobilePlayerCollapsed(false)}
                role="button"
                aria-label="Expand player"
              />
            )}
            
            {/* Player container with smooth slide transition */}
            <div 
              className={`relative z-10 transition-all duration-500 ease-out ${
                isMobilePlayerCollapsed 
                  ? 'glass-card border-t border-border/50 p-3 pb-safe' 
                  : 'h-full flex flex-col items-center justify-center p-4 pt-safe pb-safe'
              }`}
            >
              {isMobilePlayerCollapsed ? (
                /* Collapsed state content */
                <>
                  {/* Expand handle at top */}
                  <div 
                    className="flex justify-center -mt-1 mb-2 cursor-pointer group"
                    onClick={() => setIsMobilePlayerCollapsed(false)}
                    role="button"
                    aria-label="Expand player"
                  >
                    <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-muted/50 group-hover:bg-muted transition-colors">
                      <ChevronUp className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                      <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">Expand</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {/* Thumbnail */}
                    <div 
                      className="h-12 w-12 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center overflow-hidden flex-shrink-0 cursor-pointer"
                      onClick={() => setIsMobilePlayerCollapsed(false)}
                      role="button"
                      aria-label="Expand player"
                    >
                      {isYouTubeRadioPlaying && currentYouTubeStream?.videoId ? (
                        <img 
                          src={`https://img.youtube.com/vi/${currentYouTubeStream.videoId}/hqdefault.jpg`}
                          alt={currentYouTubeStream?.name || "Stream"}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Music className="h-6 w-6 text-primary" />
                      )}
                    </div>
                    
                    {/* Track info */}
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => setIsMobilePlayerCollapsed(false)}
                      role="button"
                      aria-label="Expand player"
                    >
                      <h3 className="font-semibold text-sm truncate">
                        {currentYouTubeStream?.name || "Select a stream"}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <div className={`h-1.5 w-1.5 rounded-full ${
                          isAtcActive ? 'bg-green-500' : 'bg-muted-foreground'
                        }`} />
                        <span className="truncate">
                          {isAtcActive && currentAirportCode ? `ATC ${currentAirportCode}` : 'ATC OFF'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Playback Controls */}
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleSkipBack}
                        className="h-9 w-9 rounded-full"
                        aria-label="Skip to previous stream"
                      >
                        <SkipBack className="h-4 w-4" />
                      </Button>
                    
                      <Button
                        variant="default"
                        size="icon"
                        onClick={handlePlayPause}
                        className="h-10 w-10 rounded-full"
                        aria-label={(isPlaying || isYouTubeRadioPlaying) ? "Pause" : "Play"}
                      >
                        {(isPlaying || isYouTubeRadioPlaying) ? (
                          <Pause className="h-5 w-5" />
                        ) : (
                          <Play className="h-5 w-5" />
                        )}
                      </Button>
                    
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleSkipForward}
                        className="h-9 w-9 rounded-full"
                        aria-label="Skip to next stream"
                      >
                        <SkipForward className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                /* Expanded state - full player card */
                <Card className={`glass-card p-4 w-full max-w-sm relative z-10 transition-all duration-300 ${
                  isPlaying ? 'shadow-2xl' : ''
              }`}>
                <div className="space-y-3">
                  {/* Collapse handle */}
                  <div className="flex justify-center -mt-2 mb-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsMobilePlayerCollapsed(true)}
                      className="h-6 px-3 gap-1.5"
                      aria-label="Collapse player"
                    >
                      <ChevronDown className="h-3 w-3" />
                      <span className="text-xs">Minimize</span>
                    </Button>
                  </div>

                  {/* Album Art / ATC Thumbnail - Square */}
                  <div className="aspect-square rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex flex-col items-center justify-center border border-border/50 relative overflow-hidden">
                    {/* YouTube Stream Artwork */}
                    {isYouTubeRadioPlaying && currentYouTubeStream?.videoId && (
                      <div className="absolute inset-0">
                        <img 
                          src={`https://img.youtube.com/vi/${currentYouTubeStream.videoId}/maxresdefault.jpg`}
                          alt={currentYouTubeStream.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (target.src.includes('maxresdefault')) {
                              target.src = `https://img.youtube.com/vi/${currentYouTubeStream.videoId}/hqdefault.jpg`;
                            }
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />
                      </div>
                    )}
                    
                    {/* Announcement Banner */}
                    {showBanner && announcements.length > 0 && (
                      <div className="absolute top-0 left-0 right-0 bg-[hsl(0,0%,20%)] border-b border-[hsl(0,0%,30%)] p-2 animate-slide-in-right z-10">
                        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(60,100%,50%)]/5 via-[hsl(60,100%,50%)]/10 to-[hsl(60,100%,50%)]/5 animate-[slide-in-right_3s_ease-in-out_infinite]" />
                        <div className="relative flex items-center justify-between gap-2">
                          <p className="text-xs font-medium text-[hsl(60,100%,50%)] flex-1 text-center">
                            {announcements[currentAnnouncementIndex]}
                          </p>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleDismissBanner}
                            className="h-5 w-5 rounded-full hover:bg-[hsl(60,100%,50%)]/20 flex-shrink-0 text-[hsl(60,100%,50%)]"
                            aria-label="Dismiss announcement"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Live Listeners Counter */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
                      <LiveListeners />
                    </div>
                    
                    <div className="text-center space-y-2 relative z-10">
                      {isAtcActive && currentAirportCode ? (
                        <>
                          <Radio className="h-16 w-16 mx-auto text-primary animate-pulse drop-shadow-lg" />
                          <div className="text-sm font-medium text-foreground drop-shadow-md">LIVE ATC</div>
                          <div className="text-2xl font-bold text-foreground drop-shadow-md">{currentAirportCode}</div>
                        </>
                      ) : isYouTubeRadioPlaying && currentYouTubeStream ? (
                        <>
                          <Music className="h-16 w-16 mx-auto text-primary drop-shadow-lg" />
                          <div className="text-sm font-medium text-foreground drop-shadow-md">{currentYouTubeStream.name}</div>
                        </>
                      ) : (
                        <>
                          <Music className="h-16 w-16 mx-auto text-primary" />
                          <div className="text-sm font-medium text-muted-foreground">Lo-Fi Radio</div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Track Info */}
                  <div className="text-center space-y-1">
                    <h3 className="font-semibold text-lg truncate">
                      {currentYouTubeStream?.name || "Select a stream"}
                    </h3>
                    <p className="text-sm text-muted-foreground truncate">
                      {currentYouTubeStream?.description || "No stream playing"}
                    </p>
                    
                    {/* ATC Status */}
                    <div className="flex items-center justify-center gap-2 text-xs pt-2">
                      <div className={`h-2 w-2 rounded-full ${
                        isAtcLoading ? 'bg-orange-500 animate-pulse' : 
                        isAtcActive ? 'bg-green-500 animate-pulse' : 
                        'bg-red-500'
                      }`} />
                      <span className="text-muted-foreground">
                        {isAtcLoading && currentAirportCode ? `Connecting to ${currentAirportCode}` :
                         isAtcActive && currentAirportCode ? `LIVE ${currentAirportCode}` : 
                         currentAirportCode ? `${currentAirportCode} (disconnected)` : 'ATC OFF'}
                      </span>
                    </div>
                  </div>

                  {/* Playback progress or indicator */}
                  <div className="flex items-center justify-center py-2">
                    {(isPlaying || isYouTubeRadioPlaying) ? (
                      <PlayingIndicator isPlaying isLoading={isAudioLoading} className="mx-auto" />
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        Select a stream to start listening
                      </div>
                    )}
                  </div>

                  {/* Playback Controls */}
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleSkipBack}
                      className="h-10 w-10 rounded-full"
                      aria-label="Skip to previous stream"
                    >
                      <SkipBack className="h-5 w-5" />
                    </Button>
                  
                    <Button
                      variant="default"
                      size="icon"
                      onClick={handlePlayPause}
                      className="h-12 w-12 rounded-full"
                      aria-label={(isPlaying || isYouTubeRadioPlaying) ? "Pause" : "Play"}
                    >
                      {(isPlaying || isYouTubeRadioPlaying) ? (
                        <Pause className="h-6 w-6" />
                      ) : (
                        <Play className="h-6 w-6" />
                      )}
                    </Button>
                  
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleSkipForward}
                      className="h-10 w-10 rounded-full"
                      aria-label="Skip to next stream"
                    >
                      <SkipForward className="h-5 w-5" />
                    </Button>
                  </div>

                  {/* Secondary Controls - with menu button */}
                  <div className="flex items-center justify-center gap-1 border-t border-border/50 pt-3">
                    <Sheet>
                      <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Open ATC radio settings">
                          <Radio className="h-4 w-4" />
                        </Button>
                      </SheetTrigger>
                      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
                        <SheetHeader>
                          <SheetTitle>ATC Settings</SheetTitle>
                        </SheetHeader>
                        <div className="mt-6">
                          <ATCPanel onAirportSelect={onAirportSelect} />
                        </div>
                      </SheetContent>
                    </Sheet>

                    <Sheet>
                      <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 relative" aria-label="Open Pomodoro timer">
                          <Timer className="h-4 w-4" />
                          {(isPomodoroRunning || isPomodoroComplete) && (
                            <span className={`absolute -top-1 -right-1 text-[9px] font-bold px-1 py-0.5 rounded-full min-w-[18px] ${
                              isPomodoroComplete 
                                ? 'bg-primary text-primary-foreground animate-pulse' 
                                : 'bg-muted text-muted-foreground'
                            }`}>
                              {isPomodoroComplete ? "!" : formatPomodoroIndicator(pomodoroTime)}
                            </span>
                          )}
                        </Button>
                      </SheetTrigger>
                      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
                        <SheetHeader>
                          <SheetTitle className="flex items-center gap-2">
                            <Timer className="h-5 w-5" />
                            Pomodoro Timer
                          </SheetTitle>
                        </SheetHeader>
                        <div className="mt-6">
                          <PomodoroTimer />
                        </div>
                      </SheetContent>
                    </Sheet>

                    <Sheet>
                      <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Open YouTube radio streams">
                          <Music className="h-4 w-4" />
                        </Button>
                      </SheetTrigger>
                      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col max-h-screen">
                        <SheetHeader className="flex-shrink-0">
                          <SheetTitle className="flex items-center gap-2">
                            <Music className="h-5 w-5" />
                            Music
                          </SheetTitle>
                        </SheetHeader>
                        <div className="flex-1 overflow-y-auto mt-6 space-y-6 pr-2">
                          <div className="space-y-3">
                            <label className="text-sm font-medium">Volume</label>
                            <div className="flex items-center gap-3">
                              <Volume2 className="h-4 w-4 text-muted-foreground" />
                              <Slider
                                value={[musicVolume]}
                                onValueChange={(value) => setMusicVolume(value[0])}
                                max={100}
                                step={1}
                                className="flex-1"
                              />
                              <span className="text-sm text-muted-foreground w-10 text-right">
                                {Math.round(musicVolume)}%
                              </span>
                            </div>
                          </div>
                          <YouTubeStreamsStatus />
                        </div>
                      </SheetContent>
                    </Sheet>

                    <Sheet>
                      <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Open volume settings">
                          <Volume2 className="h-4 w-4" />
                        </Button>
                      </SheetTrigger>
                      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
                        <SheetHeader>
                          <SheetTitle>Volume Control</SheetTitle>
                        </SheetHeader>
                        <div className="mt-6 space-y-6">
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <label className="text-sm font-medium">Music Volume</label>
                              <span className="text-xs text-muted-foreground">{musicVolume}%</span>
                            </div>
                            <Slider
                              value={[musicVolume]}
                              onValueChange={(value) => setMusicVolume(value[0])}
                              max={100}
                              step={1}
                            />
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <label className="text-sm font-medium">ATC Volume</label>
                              <span className="text-xs text-muted-foreground">{soundVolume}%</span>
                            </div>
                            <Slider
                              value={[soundVolume]}
                              onValueChange={(value) => setSoundVolume(value[0])}
                              max={100}
                              step={1}
                            />
                          </div>
                        </div>
                      </SheetContent>
                    </Sheet>

                    {/* Menu button integrated here */}
                    <MobileMenuButton />
                  </div>

                  {/* SEO headings - subtle, below icons on mobile */}
                  <div className="pt-2 opacity-40 text-center">
                    <h1 className="text-[9px] font-medium text-foreground/70 leading-tight">
                      Sky Lofi ATC – Lofi Beats with Live Air Traffic Control
                    </h1>
                    <h2 className="text-[8px] text-foreground/50 leading-tight">
                      Free 24/7 study music for coding, work &amp; relaxation
                    </h2>
                  </div>
                </div>
              </Card>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // Desktop layout (unchanged)
  return (
    <>
      {/* Hidden YouTube Radio - auto-plays on first play */}
      <div className="absolute w-px h-px -m-px overflow-hidden opacity-0 pointer-events-none -z-10" aria-hidden>
        <YouTubeRadio 
          volume={musicVolume} 
          onVolumeChange={setMusicVolume}
          onPlayingChange={setIsYouTubeRadioPlaying}
          autoPlay={isPlaying}
          onStreamChange={setCurrentYouTubeStream}
        />
      </div>

      <div className="fixed inset-0 flex items-center justify-center pointer-events-none p-3 sm:p-6">
        {/* Backdrop dimmer when playing */}
        <div 
          className={`fixed inset-0 bg-black/30 transition-opacity duration-700 ${
            isPlaying ? 'opacity-100' : 'opacity-0'
          }`}
        />
        
        <div className="relative pointer-events-auto w-full max-w-[95vw] sm:max-w-xs">
        {/* RGB Border Animation - Vibrant edge with blurred fade */}
        {isPlaying && (
          <div 
            className="absolute -inset-[25px] rounded-2xl opacity-75 blur-2xl"
            style={{
              background: 'conic-gradient(from 0deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3, #ff0000)',
              animation: 'rgb-border 3s linear infinite',
              maskImage: 'linear-gradient(to right, transparent, black 25px, black calc(100% - 25px), transparent), linear-gradient(to bottom, transparent, black 25px, black calc(100% - 25px), transparent)',
              WebkitMaskImage: 'linear-gradient(to right, transparent, black 25px, black calc(100% - 25px), transparent), linear-gradient(to bottom, transparent, black 25px, black calc(100% - 25px), transparent)',
              maskComposite: 'intersect',
              WebkitMaskComposite: 'source-in'
            }}
          />
        )}
        
          <Card className={`glass-card p-3 sm:p-4 md:p-6 w-full relative z-10 transition-all duration-300 ${
            isPlaying ? 'shadow-2xl' : ''
          }`}>
            <div className="space-y-3 sm:space-y-4">
            {/* Album Art / ATC Thumbnail - Square */}
            <div className="aspect-square rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex flex-col items-center justify-center border border-border/50 relative overflow-hidden">
            {/* YouTube Stream Artwork */}
            {isYouTubeRadioPlaying && currentYouTubeStream?.videoId && (
              <div className="absolute inset-0">
                <img 
                  src={`https://img.youtube.com/vi/${currentYouTubeStream.videoId}/maxresdefault.jpg`}
                  alt={currentYouTubeStream.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Fallback to high quality thumbnail if max resolution fails
                    const target = e.target as HTMLImageElement;
                    if (target.src.includes('maxresdefault')) {
                      target.src = `https://img.youtube.com/vi/${currentYouTubeStream.videoId}/hqdefault.jpg`;
                    }
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/20 to-transparent" />
              </div>
            )}
            
            {/* Announcement Banner */}
            {showBanner && announcements.length > 0 && (
              <div className="absolute top-0 left-0 right-0 bg-[hsl(0,0%,20%)] border-b border-[hsl(0,0%,30%)] p-2 animate-slide-in-right z-10">
                <div className="absolute inset-0 bg-gradient-to-r from-[hsl(60,100%,50%)]/5 via-[hsl(60,100%,50%)]/10 to-[hsl(60,100%,50%)]/5 animate-[slide-in-right_3s_ease-in-out_infinite]" />
                <div className="relative flex items-center justify-between gap-2">
                  <p className="text-xs font-medium text-[hsl(60,100%,50%)] flex-1 text-center">
                    {announcements[currentAnnouncementIndex]}
                  </p>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleDismissBanner}
                    className="h-5 w-5 rounded-full hover:bg-[hsl(60,100%,50%)]/20 flex-shrink-0 text-[hsl(60,100%,50%)]"
                    aria-label="Dismiss announcement"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            )}
            
            {/* Live Listeners Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
              <LiveListeners />
            </div>
            
            <div className="text-center space-y-2 relative z-10">
              {isAtcActive && currentAirportCode ? (
                <>
                  <Radio className="h-16 w-16 mx-auto text-primary animate-pulse drop-shadow-lg" />
                  <div className="text-sm font-medium text-foreground drop-shadow-md">LIVE ATC</div>
                  <div className="text-2xl font-bold text-foreground drop-shadow-md">{currentAirportCode}</div>
                </>
              ) : isYouTubeRadioPlaying && currentYouTubeStream ? (
                <>
                  <Music className="h-16 w-16 mx-auto text-primary drop-shadow-lg" />
                  <div className="text-sm font-medium text-foreground drop-shadow-md">{currentYouTubeStream.name}</div>
                </>
              ) : (
                <>
                  <Music className="h-16 w-16 mx-auto text-primary" />
                  <div className="text-sm font-medium text-muted-foreground">Lo-Fi Radio</div>
                </>
              )}
            </div>
          </div>

          {/* Track Info */}
          <div className="text-center space-y-1">
            <h3 className="font-semibold text-lg truncate">
              {currentYouTubeStream?.name || "Select a stream"}
            </h3>
            <p className="text-sm text-muted-foreground truncate">
              {currentYouTubeStream?.description || "No stream playing"}
            </p>
            
            {/* ATC Status - Always visible */}
            <div className="flex items-center justify-center gap-2 text-xs pt-2">
              <div className={`h-2 w-2 rounded-full ${
                isAtcLoading ? 'bg-orange-500 animate-pulse' : 
                isAtcActive ? 'bg-green-500 animate-pulse' : 
                'bg-red-500'
              }`} />
              <span className="text-muted-foreground">
                {isAtcLoading && currentAirportCode ? `Connecting to ${currentAirportCode}` :
                 isAtcActive && currentAirportCode ? `LIVE ${currentAirportCode}` : 
                 currentAirportCode ? `${currentAirportCode} (disconnected)` : 'ATC OFF'}
              </span>
            </div>

          </div>

          {/* Playback progress or indicator */}
          <div className="flex items-center justify-center py-2">
            {(isPlaying || isYouTubeRadioPlaying) ? (
              <PlayingIndicator isPlaying isLoading={isAudioLoading} className="mx-auto" />
            ) : (
              <div className="text-sm text-muted-foreground">
                Select a stream to start listening
              </div>
            )}
          </div>

            {/* Playback Controls - Removed shuffle and repeat */}
            <TooltipProvider delayDuration={200}>
              <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
              
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleSkipBack}
                      className="h-10 w-10 sm:h-12 sm:w-12 rounded-full"
                      aria-label="Skip to previous stream"
                    >
                      <SkipBack className="h-5 w-5 sm:h-6 sm:w-6" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Previous Stream</TooltipContent>
                </Tooltip>
              
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="default"
                      size="icon"
                      onClick={handlePlayPause}
                      className="h-12 w-12 sm:h-14 sm:w-14 rounded-full"
                      aria-label={(isPlaying || isYouTubeRadioPlaying) ? "Pause" : "Play"}
                    >
                      {(isPlaying || isYouTubeRadioPlaying) ? (
                        <Pause className="h-6 w-6 sm:h-7 sm:w-7" />
                      ) : (
                        <Play className="h-6 w-6 sm:h-7 sm:w-7" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{isPlaying ? 'Pause' : 'Play'}</TooltipContent>
                </Tooltip>
              
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleSkipForward}
                      className="h-10 w-10 sm:h-12 sm:w-12 rounded-full"
                      aria-label="Skip to next stream"
                    >
                      <SkipForward className="h-5 w-5 sm:h-6 sm:w-6" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Next Stream</TooltipContent>
                </Tooltip>

                </div>
              </TooltipProvider>

              {/* Secondary Controls */}
              <TooltipProvider delayDuration={200}>
                <div className="flex items-center justify-center gap-1 sm:gap-2 border-t border-border/50 pt-3 sm:pt-4 flex-wrap">
                <Sheet>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-10 sm:w-10" aria-label="Open ATC radio settings">
                          <Radio className="h-4 w-4 sm:h-5 sm:w-5" />
                        </Button>
                      </SheetTrigger>
                    </TooltipTrigger>
                    <TooltipContent>Select Airport</TooltipContent>
                  </Tooltip>
                  <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
                    <SheetHeader>
                      <SheetTitle>ATC Settings</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      <ATCPanel onAirportSelect={onAirportSelect} />
                    </div>
                  </SheetContent>
                </Sheet>

                <Sheet>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-10 sm:w-10 relative" aria-label="Open Pomodoro timer">
                          <Timer className="h-4 w-4 sm:h-5 sm:w-5" />
                          {(isPomodoroRunning || isPomodoroComplete) && (
                            <span className={`absolute -top-1 -right-1 text-[9px] font-bold px-1 py-0.5 rounded-full min-w-[18px] ${
                              isPomodoroComplete 
                                ? 'bg-primary text-primary-foreground animate-pulse' 
                                : 'bg-muted text-muted-foreground'
                            }`}>
                              {isPomodoroComplete ? "!" : formatPomodoroIndicator(pomodoroTime)}
                            </span>
                          )}
                        </Button>
                      </SheetTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isPomodoroComplete ? "Time's Up!" : isPomodoroRunning ? `Timer: ${formatPomodoroIndicator(pomodoroTime)}` : "Pomodoro Timer"}
                    </TooltipContent>
                  </Tooltip>
                  <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
                    <SheetHeader>
                      <SheetTitle className="flex items-center gap-2">
                        <Timer className="h-5 w-5" />
                        Pomodoro Timer
                      </SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      <PomodoroTimer />
                    </div>
                  </SheetContent>
                </Sheet>

                <Sheet>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-10 sm:w-10" aria-label="Open YouTube radio streams">
                          <Music className="h-4 w-4 sm:h-5 sm:w-5" />
                        </Button>
                      </SheetTrigger>
                    </TooltipTrigger>
                    <TooltipContent>Music Stream</TooltipContent>
                  </Tooltip>
                  <SheetContent side="right" className="w-full sm:max-w-md flex flex-col max-h-screen">
                    <SheetHeader className="flex-shrink-0">
                      <SheetTitle className="flex items-center gap-2">
                        <Music className="h-5 w-5" />
                        Music
                      </SheetTitle>
                    </SheetHeader>
                    <div className="flex-1 overflow-y-auto mt-6 space-y-6 pr-2">
                      <div className="space-y-3">
                        <label className="text-sm font-medium">Volume</label>
                        <div className="flex items-center gap-3">
                          <Volume2 className="h-4 w-4 text-muted-foreground" />
                          <Slider
                            value={[musicVolume]}
                            onValueChange={(value) => setMusicVolume(value[0])}
                            max={100}
                            step={1}
                            className="flex-1"
                          />
                          <span className="text-sm text-muted-foreground w-10 text-right">
                            {Math.round(musicVolume)}%
                          </span>
                        </div>
                      </div>
                      <YouTubeStreamsStatus />
                    </div>
                  </SheetContent>
                </Sheet>

                <Sheet>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-10 sm:w-10" aria-label="Open volume settings">
                          <Volume2 className="h-4 w-4 sm:h-5 sm:w-5" />
                        </Button>
                      </SheetTrigger>
                    </TooltipTrigger>
                    <TooltipContent>Volume</TooltipContent>
                  </Tooltip>
                <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Volume Control</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-6">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium">Music Volume</label>
                        <span className="text-xs text-muted-foreground">{musicVolume}%</span>
                      </div>
                      <Slider
                        value={[musicVolume]}
                        onValueChange={(value) => setMusicVolume(value[0])}
                        max={100}
                        step={1}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium">ATC Volume</label>
                        <span className="text-xs text-muted-foreground">{soundVolume}%</span>
                      </div>
                      <Slider
                        value={[soundVolume]}
                        onValueChange={(value) => setSoundVolume(value[0])}
                        max={100}
                        step={1}
                      />
                    </div>
                  </div>
                </SheetContent>
              </Sheet>

              </div>
            </TooltipProvider>

          </div>
        </Card>
      </div>
    </div>
    </>
  );
};
